import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useProjects, useDeleteProject } from '../../hooks/useProjects';
import { Search, Plus, Edit2, Trash2, LayoutList, LayoutGrid, Eye, EyeOff, MapPin, Calendar } from 'lucide-react';

export function AdminProjectList() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const { data, isLoading, error } = useProjects(0, 100);
  const deleteMutation = useDeleteProject();

  const filteredProjects = (data?.content || []).filter(p =>
    p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.location?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDelete = async (id: number, title: string) => {
    if (confirm(`${t('admin.deleteConfirm')}\n\n"${title}"`)) {
      try {
        await deleteMutation.mutateAsync(id);
      } catch (error) {
        console.error('Delete failed:', error);
        alert('Failed to delete project. Please try again.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-white pt-32 pb-16">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-16">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="bg-white p-10 mb-12 shadow-lg shadow-black/5"
        >
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-10">
            <div>
              <h1 className="text-5xl tracking-[0.1em] uppercase font-light mb-2">
                {t('projects.title')}{' '}
                <span className="italic font-serif">{t('admin.management')}</span>
              </h1>
              <p className="text-neutral-600 tracking-wide">
                {filteredProjects.length} {t('projects.projectCount')}
              </p>
            </div>

            <div className="flex gap-3">
              <Link
                to="/yonetim"
                className="px-6 py-3 bg-neutral-800 hover:bg-neutral-900 text-white tracking-[0.2em] uppercase text-sm transition-colors duration-300 flex items-center gap-2"
              >
                Dashboard
              </Link>
              <Link
                to="/yonetim/projects/new"
                className="px-6 py-3 bg-neutral-900 hover:bg-black text-white tracking-[0.2em] uppercase text-sm transition-colors duration-300 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                {t('admin.addProject')}
              </Link>
            </div>
          </div>

          {/* Search & View Toggle */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
              <input
                type="text"
                placeholder={t('admin.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-neutral-900 transition-shadow"
              />
            </div>
            <div className="flex gap-2 bg-neutral-100 p-1">
              <button
                onClick={() => setViewMode('list')}
                className={`p-4 transition-colors duration-300 ${viewMode === 'list' ? 'bg-white shadow-md' : 'hover:bg-neutral-200'}`}
              >
                <LayoutList className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-4 transition-colors duration-300 ${viewMode === 'grid' ? 'bg-white shadow-md' : 'hover:bg-neutral-200'}`}
              >
                <LayoutGrid className="w-5 h-5" />
              </button>
            </div>
          </div>
        </motion.div>

        {/* Loading */}
        {isLoading && (
          <div className="text-center py-32">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-12 h-12 border-4 border-neutral-200 border-t-neutral-900 rounded-full mx-auto"
            />
            <p className="mt-6 text-neutral-500 tracking-wide">{t('common.loading')}</p>
          </div>
        )}

        {/* Error */}
        {error && !isLoading && (
          <div className="bg-red-50 border-2 border-red-200 p-12 rounded text-center">
            <h3 className="text-2xl tracking-wide mb-4 text-red-800">Backend Bağlantı Hatası</h3>
            <p className="text-red-600">API sunucusuna bağlanılamıyor.</p>
          </div>
        )}

        {/* List View */}
        {!isLoading && viewMode === 'list' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="bg-white shadow-lg shadow-black/5 overflow-hidden"
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-neutral-50 border-b-2 border-neutral-200">
                  <tr>
                    <th className="text-left px-8 py-5 uppercase text-xs tracking-wide font-medium">{t('projects.project')}</th>
                    <th className="text-left px-8 py-5 uppercase text-xs tracking-wide font-medium">{t('projects.category')}</th>
                    <th className="text-left px-8 py-5 uppercase text-xs tracking-wide font-medium">{t('projects.location')}</th>
                    <th className="text-left px-8 py-5 uppercase text-xs tracking-wide font-medium">{t('projects.year')}</th>
                    <th className="text-left px-8 py-5 uppercase text-xs tracking-wide font-medium">{t('projects.images')}</th>
                    <th className="text-left px-8 py-5 uppercase text-xs tracking-wide font-medium">{t('admin.visible')}</th>
                    <th className="text-left px-8 py-5 uppercase text-xs tracking-wide font-medium">{t('admin.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProjects.map((project) => (
                    <motion.tr
                      key={project.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      whileHover={{ backgroundColor: 'rgba(0,0,0,0.02)' }}
                      className="border-b border-neutral-100 transition-colors"
                    >
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-4">
                          <div className="w-20 h-20 bg-neutral-100 overflow-hidden flex-shrink-0">
                            {project.images?.[0] ? (
                              <img src={project.images[0].imageUrl} alt={project.title} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-neutral-300 text-xs">No img</div>
                            )}
                          </div>
                          <span className="tracking-wide">{project.title}</span>
                        </div>
                      </td>
                      <td className="px-8 py-6 text-neutral-600">{project.category || '—'}</td>
                      <td className="px-8 py-6 text-neutral-600">{project.location || '—'}</td>
                      <td className="px-8 py-6 text-neutral-600">{project.year || '—'}</td>
                      <td className="px-8 py-6 text-neutral-600">{project.images?.length || 0}</td>
                      <td className="px-8 py-6">
                        {project.isVisible ? (
                          <span className="flex items-center gap-2 text-green-600"><Eye className="w-4 h-4" />{t('admin.yes')}</span>
                        ) : (
                          <span className="flex items-center gap-2 text-neutral-400"><EyeOff className="w-4 h-4" />{t('admin.no')}</span>
                        )}
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex gap-2">
                          <Link to={`/yonetim/projects/${project.id}/edit`}>
                            <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} className="p-3 hover:bg-neutral-100 transition-colors rounded">
                              <Edit2 className="w-4 h-4" />
                            </motion.button>
                          </Link>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleDelete(project.id, project.title)}
                            className="p-3 hover:bg-red-50 text-red-600 transition-colors rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </motion.button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredProjects.length === 0 && (
              <div className="text-center py-16 text-neutral-400">
                <p className="tracking-wide">Proje bulunamadı</p>
              </div>
            )}
          </motion.div>
        )}

        {/* Grid View */}
        {!isLoading && viewMode === 'grid' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
          >
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                whileHover={{ y: -8 }}
                className="bg-white shadow-lg shadow-black/5 hover:shadow-xl hover:shadow-black/10 transition-shadow duration-500 overflow-hidden group"
              >
                <div className="aspect-[4/3] bg-neutral-100 overflow-hidden relative">
                  {project.images?.[0] ? (
                    <img src={project.images[0].imageUrl} alt={project.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-neutral-300">No image</div>
                  )}
                  {!project.isVisible && (
                    <div className="absolute top-4 left-4 px-3 py-1 bg-black/70 text-white text-xs uppercase tracking-wide flex items-center gap-1">
                      <EyeOff className="w-3 h-3" /> Gizli
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="tracking-wide uppercase text-sm mb-2 truncate">{project.title}</h3>
                  <div className="flex items-center gap-3 text-xs text-neutral-500 mb-4">
                    {project.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{project.location}</span>}
                    {project.year && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{project.year}</span>}
                  </div>
                  <div className="flex gap-2">
                    <Link to={`/yonetim/projects/${project.id}/edit`} className="flex-1">
                      <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="w-full px-4 py-3 bg-neutral-900 text-white hover:bg-neutral-800 text-xs uppercase tracking-wide transition-colors flex items-center justify-center gap-2">
                        <Edit2 className="w-3 h-3" /> {t('admin.edit')}
                      </motion.button>
                    </Link>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleDelete(project.id, project.title)}
                      className="px-4 py-3 border border-red-600 text-red-600 hover:bg-red-50 text-xs uppercase tracking-wide transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
}
